import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { PersonLinkService } from '../../services/personLinkService';
import { UserListItem } from '../../types/user';
import { useUser } from '../../contexts/AuthContext';
import UserCard from '../../components/admin/UserCard';
import UserTable from '../../components/admin/UserTable';

/**
 * UserManagement Component
 * Main page for managing all users in the system
 *
 * Features:
 * - Search users by name or email
 * - Filter by role, status, verification
 * - Switch between card and table view
 * - Bulk actions (suspend, delete, change role)
 * - Export user list
 * - Approve/reject/suspend/delete users
 * - Pagination
 */
const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useUser();

  // State
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [verificationFilter, setVerificationFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  /**
   * Load users on mount
   */
  useEffect(() => {
    loadUsers();
  }, []);

  /**
   * Apply filters when they change
   */
  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, roleFilter, statusFilter, verificationFilter, activeTab]);

  /**
   * Load all users
   */
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { users: loadedUsers } = await adminService.getAllUsers({}, 1000);
      setUsers(loadedUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get user status from flags
   */
  const getUserStatus = (user: UserListItem): string => {
    if (!user.isApproved) return 'pending';
    return 'approved';
  };

  /**
   * Apply filters to user list
   */
  const applyFilters = () => {
    let filtered = [...users];

    // Tab filter (all, pending, approved)
    if (activeTab === 1) {
      filtered = filtered.filter((user) => !user.isApproved);
    } else if (activeTab === 2) {
      filtered = filtered.filter((user) => user.isApproved);
    }
    // Note: activeTab === 3 (suspended) removed since UserListItem doesn't have isSuspended

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.displayName.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((user) => getUserStatus(user) === statusFilter);
    }

    // Verification filter
    if (verificationFilter === 'verified') {
      filtered = filtered.filter((user) => user.isEmailVerified);
    } else if (verificationFilter === 'unverified') {
      filtered = filtered.filter((user) => !user.isEmailVerified);
    }

    setFilteredUsers(filtered);
  };

  /**
   * Handle approve user
   */
  const handleApprove = async (userId: string) => {
    if (!currentUser?.uid) return;

    try {
      await adminService.approveUser(userId, currentUser.uid);
      showSnackbar('User approved successfully', 'success');
      await loadUsers();
    } catch (err: any) {
      console.error('Error approving user:', err);
      showSnackbar(err.message || 'Failed to approve user', 'error');
    }
  };

  /**
   * Handle reject user
   */
  const handleReject = (userId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Reject User',
      message: 'Are you sure you want to reject this user registration?',
      action: async () => {
        if (!currentUser?.uid) return;

        try {
          await adminService.rejectUser(userId, currentUser.uid);
          showSnackbar('User rejected successfully', 'success');
          await loadUsers();
        } catch (err: any) {
          console.error('Error rejecting user:', err);
          showSnackbar(err.message || 'Failed to reject user', 'error');
        }
      },
    });
  };

  /**
   * Handle suspend user
   */
  const handleSuspend = (userId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Suspend User',
      message: 'Are you sure you want to suspend this user? They will lose access to the system.',
      action: async () => {
        if (!currentUser?.uid) return;

        try {
          await adminService.suspendUser(
            userId,
            currentUser.uid,
            currentUser.displayName || 'Unknown Admin',
            'Suspended by admin'
          );
          showSnackbar('User suspended successfully', 'success');
          await loadUsers();
        } catch (err: any) {
          console.error('Error suspending user:', err);
          showSnackbar(err.message || 'Failed to suspend user', 'error');
        }
      },
    });
  };

  /**
   * Handle delete user
   */
  const handleDelete = (userId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message:
        'Are you sure you want to delete this user? This action cannot be undone and will remove all their data.',
      action: async () => {
        if (!currentUser?.uid) return;

        try {
          await adminService.deleteUser(
            userId,
            currentUser.uid,
            currentUser.displayName || 'Unknown Admin',
            'Deleted by admin'
          );
          showSnackbar('User deleted successfully', 'success');
          await loadUsers();
        } catch (err: any) {
          console.error('Error deleting user:', err);
          showSnackbar(err.message || 'Failed to delete user', 'error');
        }
      },
    });
  };

  /**
   * Handle edit user
   */
  const handleEdit = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };

  /**
   * Handle unlink user
   */
  const handleUnlink = (userId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Unlink User',
      message: 'Are you sure you want to unlink this user from their person profile?',
      action: async () => {
        if (!currentUser?.uid) return;

        try {
          await PersonLinkService.unlinkUser(
            userId,
            currentUser.uid,
            currentUser.displayName || 'Unknown Admin',
            'Unlinked by admin'
          );
          showSnackbar('User unlinked successfully', 'success');
          await loadUsers();
        } catch (err: any) {
          console.error('Error unlinking user:', err);
          showSnackbar(err.message || 'Failed to unlink user', 'error');
        }
      },
    });
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Close snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Close confirm dialog
   */
  const handleCloseConfirmDialog = () => {
    setConfirmDialog(null);
  };

  /**
   * Execute confirm dialog action
   */
  const handleConfirmDialogAction = () => {
    if (confirmDialog?.action) {
      confirmDialog.action();
    }
    handleCloseConfirmDialog();
  };

  /**
   * Export users to CSV
   */
  const handleExport = () => {
    const csv = [
      ['Name', 'Email', 'Role', 'Status', 'Email Verified', 'Created At', 'Last Login'].join(','),
      ...filteredUsers.map((user) =>
        [
          `"${user.displayName}"`,
          user.email,
          user.role,
          getUserStatus(user),
          user.isEmailVerified ? 'Yes' : 'No',
          user.createdAt?.toISOString() || '',
          user.lastLoginAt?.toISOString() || '',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-export-${new Date().toISOString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    showSnackbar('User list exported successfully', 'success');
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all users, approve registrations, and control access
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters and Actions */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Role Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="contributor">Contributor</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Verification Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Verification</InputLabel>
                <Select
                  value={verificationFilter}
                  onChange={(e) => setVerificationFilter(e.target.value)}
                  label="Verification"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* View Mode Toggle */}
            <Grid item xs={12} sm={6} md={2}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, value) => value && setViewMode(value)}
                  size="small"
                >
                  <ToggleButton value="grid">
                    <ViewModuleIcon />
                  </ToggleButton>
                  <ToggleButton value="table">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                >
                  Export
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label={`All Users (${users.length})`} />
            <Tab
              label={`Pending (${users.filter((u) => !u.isApproved).length})`}
            />
            <Tab
              label={`Active (${users.filter((u) => u.isApproved).length})`}
            />
          </Tabs>
        </Paper>

        {/* User List */}
        {filteredUsers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No users found matching your filters
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {filteredUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user.uid}>
                <UserCard
                  user={user}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEdit={handleEdit}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <UserTable
            users={filteredUsers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSuspend={handleSuspend}
            onApprove={handleApprove}
            onUnlink={handleUnlink}
            onSelectionChange={setSelectedUsers}
            selectable={false}
            showActions={true}
          />
        )}

        {/* Confirm Dialog */}
        <Dialog
          open={confirmDialog?.open || false}
          onClose={handleCloseConfirmDialog}
        >
          <DialogTitle>{confirmDialog?.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog?.message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
            <Button onClick={handleConfirmDialogAction} color="error" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default UserManagement;
